/**
 * Sprint V100 (17.09.2026) — Zentrale QuickCheck-Component (Likert + Kontext).
 * Sprint V100.1 (17.09.2026) — UX-Framework-Alignment mit 20-Fragen-Test:
 * eine Frage sichtbar, Auto-Advance, Zurueck-Button, Progress-Bar.
 *
 * State-Machine:
 *   step: "context" | "questions" | "result" | "error"
 *
 * Verantwortlich fuer:
 *   - Vorab-Frage (Beruflich vs. Privat)
 *   - Progressive Frage-fuer-Frage-Beantwortung mit Auto-Advance
 *   - Submit an Backend
 *   - Ergebnis-Rendering mit A/B-Test-V99-Framework-Anschluss
 *   - CAPI-Events via Backend, GA4-Events client-side
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { trackFokustypAssigned } from "@/lib/ga4";
import { QUICKCHECK_V100 } from "../../data/quickCheckV100Data";
import QuickCheckContextChooser from "./QuickCheckContextChooser";
import QuickCheckLikertQuestion from "./QuickCheckLikertQuestion";
import QuickCheckResult from "./QuickCheckResult";

const V99_TEST_NAME = "v99_bridge_video";
const ADVANCE_DELAY_MS = 250;

// UTM aus URL extrahieren — datensparsam, nur die 5 Standardfelder.
function extractUTM() {
  try {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    ["source", "medium", "campaign", "content", "term"].forEach((k) => {
      const v = params.get(`utm_${k}`);
      if (v) out[k] = v;
    });
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

function getOrCreateSessionId() {
  try {
    let sid = localStorage.getItem("f4s_session_id");
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("f4s_session_id", sid);
    }
    return sid;
  } catch {
    return `s_${Date.now()}`;
  }
}

// GA4-Event fire-and-forget.
function trackGA4(eventName, params) {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, params || {});
    }
  } catch {
    /* noop */
  }
}

export default function QuickCheckV100({
  source = "landing_page_quicktest",
  onMagicLink,
  onBackToCockpit,
  onResult,
}) {
  const [step, setStep] = useState("context");
  const [context, setContext] = useState(null);
  const [answers, setAnswers] = useState({});     // { qid: value }
  const [index, setIndex] = useState(0);
  const [pendingValue, setPendingValue] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [abVariant, setAbVariant] = useState(null);

  const sessionId = useMemo(getOrCreateSessionId, []);
  const utm = useMemo(extractUTM, []);

  const questions = context ? QUICKCHECK_V100[context] : [];
  const total = questions.length;
  const currentQ = questions[index];

  // A/B-Test-V99 Assignment (fire-and-forget, deterministisch im Backend).
  useEffect(() => {
    if (!sessionId) return;
    api
      .get(`/ab-test/assign`, { params: { session_id: sessionId, test: V99_TEST_NAME } })
      .then((r) => setAbVariant(r?.data?.variant || null))
      .catch(() => setAbVariant(null));
  }, [sessionId]);

  useEffect(() => {
    // 05.10.2026 — Zusaetzliches `test_start`-Event, damit der GA4-Trichter
    // v1 und v2 unter EINEM Event-Namen zaehlen kann. Alt-Event
    // `quickcheck_v100_view` bleibt fuer historische Reports erhalten.
    trackGA4("quickcheck_v100_view", { source });
    trackGA4("test_start", {
      test_type: "quick_check_5",
      question_set: "v1",
      source,
    });
  }, [source]);

  const handleContextSelect = (c) => {
    setContext(c);
    setStep("questions");
    setIndex(0);
    setAnswers({});
    trackGA4("quickcheck_v100_context_selected", { context: c, source });
  };

  const submitToBackend = async (finalAnswers) => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        session_id: sessionId,
        context,
        answers: questions.map((q) => ({ qid: q.qid, value: finalAnswers[q.qid] })),
        utm,
        ab_variant_v99: abVariant || null,
        source,
      };
      const { data } = await api.post("/quickcheck/v100/submit", payload);
      setResult(data);
      // Ergebnis-Gate (/lp/pocketcoach): Ergebnis erst nach E-Mail-Bestaetigung.
      setStep(onResult ? "gated" : "result");
      if (onResult) onResult(data, sessionId);
      trackGA4("quickcheck_v100_complete", {
        context,
        primary_type: data.primary_type,
        secondary_type: data.secondary_type,
        variant_v99: abVariant || "unassigned",
      });
      // 05.10.2026 — Zusaetzliches `test_complete`-Event, damit der
      // GA4-Trichter v1 und v2 unter EINEM Event-Namen zaehlen kann.
      trackGA4("test_complete", {
        question_set: "v1",
        assigned_primary_type: data.primary_type,
        score_lead: data.fokustyp_score_delta,
        variant_v99: abVariant || "unassigned",
      });
      trackFokustypAssigned({ fokustyp: data.primary_type, questionSet: "v1" });
      try {
        await api.post("/ab-test/track/impression", {
          session_id: sessionId,
          test_name: V99_TEST_NAME,
          variant: abVariant || "A",
          fokustyp: data.primary_type,
        });
      } catch {
        /* fire-and-forget */
      }
    } catch (exc) {
      setError(
        exc?.response?.data?.detail ||
          "Etwas ging schief. Bitte versuch es gleich nochmal.",
      );
      setStep("error");
    } finally {
      setSubmitting(false);
      setPendingValue(null);
    }
  };

  // Auto-Advance nach Klick: 250 ms Delay, dann naechste Frage oder Submit.
  const handleAnswer = (value) => {
    if (pendingValue || submitting || !currentQ) return;
    setPendingValue(value);
    const next = { ...answers, [currentQ.qid]: value };
    setAnswers(next);
    trackGA4("quickcheck_v100_question_answered", { qid: currentQ.qid, value, context });

    window.setTimeout(() => {
      if (index < total - 1) {
        setIndex((i) => i + 1);
        setPendingValue(null);
      } else {
        submitToBackend(next);
      }
    }, ADVANCE_DELAY_MS);
  };

  const handleBack = () => {
    if (index === 0 || pendingValue || submitting) return;
    setIndex((i) => i - 1);
    setPendingValue(null);
  };

  const handleRestart = () => {
    setStep("context");
    setContext(null);
    setAnswers({});
    setIndex(0);
    setPendingValue(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="w-full" data-testid="quickcheck-v100-root">
      {step === "context" && (
        <QuickCheckContextChooser onSelect={handleContextSelect} />
      )}

      {step === "questions" && currentQ && (
        <div className="max-w-3xl mx-auto">
          <QuickCheckLikertQuestion
            qid={currentQ.qid}
            qIndex={index}
            qTotal={total}
            text={currentQ.text}
            value={answers[currentQ.qid] || null}
            pendingValue={pendingValue}
            submitting={submitting}
            onChange={handleAnswer}
            onBack={handleBack}
          />
        </div>
      )}

      {step === "result" && result && (
        <QuickCheckResult
          primaryType={result.primary_type}
          secondaryType={result.secondary_type}
          scores={result.scores}
          onRestart={handleRestart}
          variant={abVariant}
          onMagicLink={onMagicLink}
          onBackToCockpit={onBackToCockpit}
          followUpMode={source === "dashboard_test" ? "dashboard" : "landing"}
        />
      )}

      {step === "error" && (
        <div
          className="max-w-md mx-auto text-center bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-2xl p-6"
          data-testid="v100-error"
        >
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button
            type="button"
            onClick={handleRestart}
            className="mt-4 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-2 text-sm font-bold"
          >
            Nochmal starten
          </button>
        </div>
      )}
    </div>
  );
}
